import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, XCircle, Lock, Sparkles } from "lucide-react";

function Starfield() {
  const stars = Array.from({ length: 40 });
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {stars.map((_, i) => {
        const size = Math.random() * 2 + 1;
        return (
          <span
            key={i}
            className="absolute rounded-full bg-champagne/70 animate-pulse-soft"
            style={{
              width: size, height: size,
              top: `${Math.random() * 100}%`, left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`, animationDuration: `${2 + Math.random() * 3}s`,
              boxShadow: "0 0 6px rgba(247,231,206,0.8)",
            }}
          />
        );
      })}
    </div>
  );
}

export default function CheckoutReturn() {
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const { refresh: refreshCart } = useCart();
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);
  const firstName = (user?.name || "").split(" ")[0] || "there";

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let stop = false;
    const poll = async () => {
      if (attempts.current >= 8 || stop) { if (status === "checking") setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.checkoutStatus(sid);
        setInfo(data);
        if (data.payment_status === "paid") { setStatus("paid"); refresh?.(); refreshCart?.(); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
    return () => { stop = true; };
  }, []);

  const headline = {
    cart: "It's officially yours.",
    bump: "The lock is yours.",
    vip: "Welcome to VIP.",
    reserve: "It's officially yours.",
    credits: "Try-on credits added!",
  };
  const sub = {
    cart: "Your dress has been permanently secured out of the catalog. No one at your school will ever wear it.",
    bump: "The reservation has transferred to you. Complete your purchase to keep it forever.",
    vip: "Unlimited virtual try-ons are now unlocked for the whole season.",
    reserve: "Your dress is permanently secured at your school — and if you booked Heather, she'll see you at the atelier in Oconomowoc.",
    credits: "Your virtual try-on credits are ready. Head back to any dress and see yourself in it.",
  };

  // PROCESSING (cinematic)
  if (status === "checking") {
    return (
      <div data-testid="checkout-return-page" className="fixed inset-0 z-[80] bg-[#1a1020] flex flex-col items-center justify-center text-center px-6"
           style={{ background: "radial-gradient(ellipse at center, #2a1a35 0%, #140b1c 70%)" }}>
        <Starfield />
        <div className="relative z-10 animate-fade-up">
          <div className="w-16 h-16 mx-auto mb-8 rounded-full border-2 border-champagne/30 border-t-champagne animate-spin"
               style={{ boxShadow: "0 0 40px rgba(247,231,206,0.4)" }} />
          <p className="font-label text-[11px] text-champagne tracking-luxe mb-6">Sealing Your Reservation</p>
          <h1 className="font-display text-5xl sm:text-6xl text-ivory mb-6">One moment, {firstName}…</h1>
          <p className="font-body text-sm text-ivory/50 max-w-sm mx-auto leading-relaxed">
            Heather is personally locking your dress out of the catalog so no one else can claim it.
          </p>
        </div>
      </div>
    );
  }

  const goBack = () => {
    try {
      const last = localStorage.getItem("hb_last_tryon_product_url");
      if (last) { window.location.assign(last); return; }
    } catch { /* ignore */ }
    window.location.assign("/try-on");
  };

  // SUCCESS
  if (status === "paid") {
    const isCredits = info?.kind === "credits";
    return (
      <div data-testid="checkout-return-page" className="min-h-screen flex items-center justify-center text-center px-6 py-20"
           style={{ background: "radial-gradient(ellipse at top, #2a1a35 0%, #140b1c 80%)" }}>
        <div className="relative z-10 max-w-lg animate-fade-up">
          <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-champagne/15 flex items-center justify-center" style={{ boxShadow: "0 0 60px rgba(247,231,206,0.3)" }}>
            {info?.kind === "vip" ? <Sparkles className="w-9 h-9 text-champagne" /> : <Lock className="w-9 h-9 text-champagne" />}
          </div>
          <p className="font-label text-[11px] text-champagne tracking-luxe mb-5">
            {info?.kind === "vip" ? "VIP Unlocked" : isCredits ? "Credits Added" : "Permanently Secured"}
          </p>
          <h1 className="font-display text-5xl sm:text-6xl text-ivory mb-5">{headline[info?.kind] || "All done."}</h1>
          <p className="font-body text-sm text-ivory/60 mb-10 leading-relaxed">{sub[info?.kind] || "Thank you for shopping with Heather's."}</p>
          <div className="flex gap-3 justify-center flex-wrap">
            {isCredits ? (
              <>
                <button
                  onClick={goBack}
                  data-testid="return-back-to-dress"
                  className="bg-champagne text-ink px-8 py-3.5 font-label text-[10px] rounded-sm hover:bg-ivory transition-colors"
                >
                  Back to Your Dress
                </button>
                <Link to="/try-on/pricing" className="border border-ivory/30 text-ivory px-8 py-3.5 font-label text-[10px] rounded-sm hover:border-ivory transition-colors">View Plans</Link>
              </>
            ) : (
              <>
                <Link to="/account" data-testid="view-account-btn" className="bg-champagne text-ink px-8 py-3.5 font-label text-[10px] rounded-sm hover:bg-ivory transition-colors">View My Atelier</Link>
                <Link to="/shop" className="border border-ivory/30 text-ivory px-8 py-3.5 font-label text-[10px] rounded-sm hover:border-ivory transition-colors">Keep Shopping</Link>
              </>
            )}
          </div>
          <p className="font-body text-[11px] text-ivory/30 mt-10">A confirmation is on its way to your inbox 🌹</p>
        </div>
      </div>
    );
  }

  // ERROR / TIMEOUT
  return (
    <div data-testid="checkout-return-page" className="min-h-screen flex flex-col items-center justify-center text-center px-6"
         style={{ background: "radial-gradient(ellipse at top, #2a1a35 0%, #140b1c 80%)" }}>
      <XCircle className="w-14 h-14 text-ivory/40 mb-6" />
      <h1 className="font-display text-4xl text-ivory mb-3">{status === "timeout" ? "Still processing…" : "Payment not completed"}</h1>
      <p className="font-body text-sm text-ivory/50 mb-8">{status === "timeout" ? "Hang tight — check your account in a moment." : "No charge was made. Your bag is safe."}</p>
      <Link to="/cart" className="bg-champagne text-ink px-8 py-3.5 font-label text-[10px] rounded-sm">Back to Bag</Link>
    </div>
  );
}
